import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Leaf, FlaskConical, Target, Award, Check, ArrowRight, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { api, eur } from "@/lib/api";
import { useSiteMedia } from "@/lib/siteImages";
import Reveal from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="hero-mask-line">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Hero() {
  const { t } = useLanguage();
  const media = useSiteMedia();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-navy noise-bg">
      <motion.div style={{ y: bgY }} className="spotlight absolute inset-0" />
      <span
        aria-hidden
        className="text-ghost-light pointer-events-none absolute -bottom-12 end-0 hidden select-none font-display text-[20vw] italic leading-none lg:block"
      >
        Antalia
      </span>
      <div className="relative mx-auto grid max-w-7xl gap-12 px-6 pb-24 pt-24 lg:grid-cols-12 lg:gap-8 lg:pt-28">
        <div className="flex flex-col justify-center lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            data-testid="hero-kicker"
            className="label-luxe text-gold"
          >
            {t.hero.kicker}
          </motion.p>
          <h1 className="mt-8 font-display text-5xl font-light leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.35}>{t.hero.l1}</MaskedLine>
            <MaskedLine delay={0.5} className="italic text-gold-light">
              {t.hero.l2}
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            data-testid="hero-subtitle"
            className="mt-8 max-w-lg text-base font-light leading-relaxed text-white/55"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <Link to="/shop" data-testid="hero-cta-shop" className="btn-gold-luxe">
              {t.hero.cta1}
              <ArrowRight size={14} className="rtl:rotate-180" />
            </Link>
            <Link to="/science" data-testid="hero-cta-science" className="btn-outline-light">
              {t.hero.cta2}
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-12 text-[10px] font-medium uppercase tracking-[0.35em] text-white/30"
          >
            GR · DE · BG · CA · AU · US
          </motion.p>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="frame-corners overflow-hidden border border-white/10">
              <img
                src={media("home-hero")}
                alt="Antalia Pharma premium supplement"
                data-testid="hero-product-image"
                className="aspect-[4/5] w-full object-cover"
              />
            </motion.div>
            <div className="absolute -bottom-6 -start-6 hidden bg-white px-6 py-5 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.25)] lg:block">
              <p className="label-luxe text-gold-dark">Est. 2015</p>
              <p className="mt-1 font-display text-lg text-navy">Marousi, Athens</p>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="relative mx-auto flex max-w-7xl justify-center px-6 pb-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="flex flex-col items-center gap-3"
        >
          <span className="label-luxe text-white/30">Scroll</span>
          <div className="scroll-line" />
        </motion.div>
      </div>
      <div className="gold-hairline relative" />
    </section>
  );
}

function TrustBar() {
  const { t } = useLanguage();
  const items = [
    { icon: Leaf, title: t.trust.t1, sub: t.trust.s1 },
    { icon: FlaskConical, title: t.trust.t2, sub: t.trust.s2 },
    { icon: Target, title: t.trust.t3, sub: t.trust.s3 },
    { icon: Award, title: t.trust.t4, sub: t.trust.s4 },
  ];
  return (
    <section data-testid="trust-bar" className="border-b border-navy/10 bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
        {items.map(({ icon: Icon, title, sub }, i) => (
          <Reveal key={i} delay={i} className={`flex items-center gap-4 px-6 py-8 ${i > 0 ? "border-s border-navy/10" : ""} ${i >= 2 ? "border-t border-navy/10 lg:border-t-0" : ""}`}>
            <Icon size={22} strokeWidth={1.25} className="shrink-0 text-gold" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-navy">{title}</p>
              <p className="mt-1 text-[11px] font-light text-navy/45">{sub}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Categories() {
  const { t } = useLanguage();
  const media = useSiteMedia();
  const cats = [
    { slug: "immune", name: t.cats.immune_n, desc: t.cats.immune_d, img: media("home-immune") },
    { slug: "focus", name: t.cats.focus_n, desc: t.cats.focus_d, img: media("home-focus") },
    { slug: "energy", name: t.cats.energy_n, desc: t.cats.energy_d, img: media("home-energy") },
    { slug: "performance", name: t.cats.performance_n, desc: t.cats.performance_d, img: media("home-performance") },
    { slug: "vitality", name: t.cats.vitality_n, desc: t.cats.vitality_d, img: "/products/arena-rapid.png" },
    { slug: "relaxation", name: t.cats.relaxation_n, desc: t.cats.relaxation_d, img: media("home-relaxation") },
  ];
  return (
    <section data-testid="categories-section" className="bg-white py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 max-w-xl">
          <Reveal><p className="label-luxe text-gold-dark">{t.cats.kicker}</p></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-5 font-display text-4xl font-light tracking-tight text-navy sm:text-5xl">{t.cats.title}</h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-5 text-sm font-light leading-relaxed text-navy/50">{t.cats.sub}</p>
          </Reveal>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {cats.map((c, i) => (
            <Reveal key={c.slug} delay={i} className={i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <Link
                to={`/shop?cat=${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className="group relative block overflow-hidden border border-navy/10"
              >
                <div className="img-zoom-frame aspect-[4/3]">
                  <img src={c.img} alt={c.name} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <span aria-hidden className="card-index pointer-events-none absolute end-5 top-4 text-6xl">
                  0{i + 1}
                </span>
                <div className="absolute inset-0 bg-navy/0 transition-colors duration-500 group-hover:bg-navy/25" />
                <div className="absolute inset-x-0 bottom-0 bg-white/90 p-6 backdrop-blur-sm transition-colors duration-500 group-hover:bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-display text-2xl font-medium text-navy">{c.name}</h3>
                      <p className="mt-1 text-xs font-light text-navy/50">{c.desc}</p>
                    </div>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-navy/15 text-navy transition-colors duration-300 group-hover:border-gold group-hover:bg-gold">
                      <ArrowRight size={16} strokeWidth={1.5} className="rtl:rotate-180" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
          <Reveal delay={5} className="flex items-center justify-center border border-dashed border-navy/15 bg-surface p-8">
            <Link to="/shop" data-testid="categories-view-all" className="group flex items-center gap-3 text-navy">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em]">{t.actions.viewAll}</span>
              <ArrowRight size={16} className="text-gold transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const { t } = useLanguage();
  return (
    <div data-testid="editorial-marquee" className="border-y border-navy/10 bg-surface py-6">
      <Marquee speed={28} gradient={false}>
        <span className="mx-8 font-display text-xl font-light italic text-navy/35">{t.marquee}</span>
        <span className="mx-8 font-display text-xl font-light italic text-navy/35">{t.marquee}</span>
      </Marquee>
    </div>
  );
}

function Manifesto() {
  const { t } = useLanguage();
  const chapters = [
    { n: "01", title: t.manifesto.m1t, text: t.manifesto.m1x },
    { n: "02", title: t.manifesto.m2t, text: t.manifesto.m2x },
    { n: "03", title: t.manifesto.m3t, text: t.manifesto.m3x },
  ];
  return (
    <section data-testid="manifesto-section" className="relative overflow-hidden bg-navy py-28 text-white noise-bg lg:py-36">
      <div className="spotlight absolute inset-0" />
      <span
        aria-hidden
        className="text-ghost-light pointer-events-none absolute inset-x-0 bottom-4 select-none text-center font-display text-[15vw] italic leading-none"
      >
        Scientia
      </span>
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mb-20">
          <Reveal><p className="label-luxe text-gold">{t.manifesto.kicker}</p></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-6 max-w-3xl font-display text-4xl font-light tracking-tight sm:text-5xl lg:text-6xl">
              {t.manifesto.title}
            </h2>
          </Reveal>
        </div>
        <div className="grid gap-px bg-white/10 lg:grid-cols-3">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i} className="bg-navy p-10 lg:p-12">
              <p className="font-display text-5xl font-light text-gold/70">{c.n}</p>
              <h3 className="mt-8 font-display text-2xl font-medium">{c.title}</h3>
              <p className="mt-4 text-sm font-light leading-relaxed text-white/50">{c.text}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={3} className="mt-14">
          <Link to="/science" data-testid="manifesto-cta" className="btn-outline-light">
            {t.manifesto.cta}
            <ArrowRight size={14} className="rtl:rotate-180" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Featured() {
  const { t } = useLanguage();
  const [product, setProduct] = useState(null);
  useEffect(() => {
    api.product("arena-rapid").then(setProduct).catch(() => {});
  }, []);
  const points = [t.featured.p1, t.featured.p2, t.featured.p3];
  return (
    <section data-testid="featured-section" className="spotlight-light bg-sage-light/40 py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2">
        <Reveal className="relative">
          <div className="img-zoom-frame border border-navy/10 bg-white">
            <img src="/products/arena-rapid.png" alt="Arena Rapid" data-testid="featured-image" className="aspect-[4/5] w-full object-cover" loading="lazy" />
          </div>
          <div className="absolute -top-5 -end-5 bg-navy px-5 py-3">
            <p className="label-luxe text-gold">{t.featured.badge}</p>
          </div>
        </Reveal>
        <div>
          <Reveal><p className="label-luxe text-gold-dark">{t.featured.kicker}</p></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-5 font-display text-4xl font-light tracking-tight text-navy sm:text-5xl">{t.featured.title}</h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-navy/55">{t.featured.desc}</p>
          </Reveal>
          <div className="mt-8 space-y-3">
            {points.map((p, i) => (
              <Reveal key={i} delay={3 + i} className="flex items-center gap-3">
                <Check size={15} className="shrink-0 text-gold-dark" />
                <span className="text-sm font-light text-navy/70">{p}</span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={6} className="mt-10">
            <Link to="/product/arena-rapid" data-testid="featured-cta" className="btn-primary-luxe">
              {t.featured.cta}
              <ArrowRight size={14} className="rtl:rotate-180" />
            </Link>
          </Reveal>
        </div>
      </div>
      {product && <span className="hidden">{product.slug}</span>}
    </section>
  );
}

function ComingSoon() {
  const { t, lang } = useLanguage();
  const [products, setProducts] = useState([]);
  const [launchDate, setLaunchDate] = useState("2027-01-01");
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    api.products().then((list) => setProducts(list.filter((p) => p.coming_soon))).catch(() => {});
    api.config().then((c) => c.launch_date && setLaunchDate(c.launch_date)).catch(() => {});
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  if (!products.length) return null;
  const diff = Math.max(0, new Date(`${launchDate}T00:00:00`) - now);
  const units = [
    [Math.floor(diff / 86400000), t.cs.days, "days"],
    [Math.floor((diff % 86400000) / 3600000), t.cs.hours, "hours"],
    [Math.floor((diff % 3600000) / 60000), t.cs.minutes, "minutes"],
    [Math.floor((diff % 60000) / 1000), t.cs.seconds, "seconds"],
  ];
  return (
    <section data-testid="coming-soon-section" className="relative overflow-hidden border-t border-gold/10 bg-navy noise-bg py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal><p className="label-luxe text-gold">{t.cs.kicker}</p></Reveal>
        <div className="mt-6 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <MaskedLine className="max-w-2xl font-display text-4xl font-light leading-tight text-white sm:text-5xl">{t.cs.title}</MaskedLine>
          <div className="flex gap-3" data-testid="launch-countdown">
            {units.map(([val, label, key]) => (
              <div key={key} data-testid={`countdown-${key}`} className="flex w-20 flex-col items-center border border-gold/25 bg-white/[0.03] px-3 py-4">
                <span className="font-display text-3xl font-light tabular-nums text-gold">{String(val).padStart(2, "0")}</span>
                <span className="mt-1 text-[9px] uppercase tracking-[0.2em] text-white/50">{label}</span>
              </div>
            ))}
          </div>
        </div>
        <Reveal delay={0.1}><p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/60">{t.cs.sub}</p></Reveal>
        <Reveal delay={0.15}>
          <Link to="/endurax" data-testid="cs-endurax-link" className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold transition-colors hover:text-white">
            {t.ex.details} →
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => {
            const loc = p.i18n?.[lang] || p.i18n?.en || {};
            return (
              <Reveal key={p.slug} delay={i * 0.05} className="h-full">
                <Link to={`/product/${p.slug}`} data-testid={`cs-card-${p.slug}`} className="group flex h-full flex-col border border-white/10 bg-white/[0.02] p-4 transition-colors duration-500 hover:border-gold/50">
                  <div className="img-zoom-frame relative aspect-square overflow-hidden bg-[#0c0c0e]">
                    <img src={p.image} alt={loc.name} className="h-full w-full object-contain" loading="lazy" />
                    <span className="absolute start-3 top-3 bg-navy px-2 py-1 text-[8px] font-bold uppercase tracking-[0.15em] text-gold">{t.prod.soonBadge}</span>
                  </div>
                  <div className="mt-4 flex flex-1 items-end justify-between gap-2">
                    <div>
                      <p className="font-display text-lg font-light text-white">{loc.name}</p>
                      <p className="mt-1 text-sm text-gold">{eur(p.price)}</p>
                    </div>
                    <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.15em] text-gold-dark transition-colors group-hover:text-gold">{t.cs.cta} →</span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}


function Bestsellers() {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.products().then((list) => setProducts(list.filter((p) => p.featured || ["immuno-prime", "mentis-focus", "performance-pro"].includes(p.slug)).slice(0, 4))).catch(() => {});
  }, []);
  if (!products.length) return null;
  return (
    <section data-testid="bestsellers-section" className="bg-white py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal><p className="label-luxe text-gold-dark">{t.shop.kicker}</p></Reveal>
            <Reveal delay={1}>
              <h2 className="mt-4 font-display text-4xl font-light tracking-tight text-navy">{t.actions.shopNow}</h2>
            </Reveal>
          </div>
          <Reveal delay={2}>
            <Link to="/shop" data-testid="bestsellers-view-all" className="group flex items-center gap-3 text-navy">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em]">{t.actions.viewAll}</span>
              <ArrowRight size={16} className="text-gold transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ArenaLineup() {
  const { t } = useLanguage();
  return (
    <section data-testid="arena-lineup-section" className="relative overflow-hidden bg-navy noise-bg">
      <div className="mx-auto max-w-7xl px-6 pt-24 text-center lg:pt-32">
        <Reveal><p className="label-luxe text-gold">{t.lineup.kicker}</p></Reveal>
        <Reveal delay={1}>
          <h2 className="mt-5 font-display text-4xl font-light tracking-tight text-white sm:text-5xl">{t.lineup.title}</h2>
        </Reveal>
        <Reveal delay={2}>
          <p className="mx-auto mt-6 max-w-2xl text-sm font-light leading-relaxed text-white/55">{t.lineup.sub}</p>
        </Reveal>
      </div>
      <Reveal delay={2} className="mx-auto max-w-5xl px-6">
        <img src="/images/arena-lineup-hero.png" alt="Arena line" data-testid="arena-lineup-image" loading="lazy" className="mx-auto w-full object-contain" />
      </Reveal>
      <div className="pb-24 pt-4 text-center lg:pb-32">
        <Reveal><Link to="/shop" data-testid="arena-lineup-cta" className="btn-gold-luxe">{t.lineup.cta}</Link></Reveal>
      </div>
    </section>
  );
}

function Testimonials() {
  const { t, lang } = useLanguage();
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.reviews(6, lang).then(setReviews).catch(() => setReviews([]));
  }, [lang]);
  const live = reviews.length > 0;
  const items = live
    ? reviews
    : [
        { q: t.testi.q1, n: t.testi.n1, r: t.testi.r1 },
        { q: t.testi.q2, n: t.testi.n2, r: t.testi.r2 },
        { q: t.testi.q3, n: t.testi.n3, r: t.testi.r3 },
      ];
  return (
    <section data-testid="testimonials-section" className="border-t border-navy/10 bg-white py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 max-w-xl">
          <Reveal><p className="label-luxe text-gold-dark">{t.testi.kicker}</p></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-5 font-display text-4xl font-light tracking-tight text-navy sm:text-5xl">{t.testi.title}</h2>
          </Reveal>
        </div>
        <div className="grid gap-px bg-navy/10 lg:grid-cols-3">
          {items.map((item, i) => (
            <Reveal key={i} delay={i % 3} className="flex flex-col justify-between bg-white p-10 lg:p-12">
              <div>
                {live ? (
                  <div className="flex items-center gap-1" data-testid={`review-stars-${i}`}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={15} className={s <= (item.rating || 5) ? "fill-gold text-gold" : "text-navy/15"} />
                    ))}
                  </div>
                ) : (
                  <span aria-hidden className="quote-mark">“</span>
                )}
                <p data-testid={live ? `review-text-${i}` : `testimonial-quote-${i}`} className="mt-6 font-display text-xl font-light italic leading-relaxed text-navy/80">
                  “{live ? item.text : item.q}”
                </p>
              </div>
              <div className="mt-10 flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 font-display text-lg text-gold-dark">
                  {(live ? item.name : item.n).charAt(0)}
                </span>
                <div>
                  <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-navy">
                    {live ? item.name : item.n}
                    {live && item.verified && (
                      <span data-testid={`review-verified-${i}`} className="border border-sage/50 bg-sage-light/60 px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.12em] text-sage-dark">
                        {t.rev.verified}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] font-light text-navy/45">
                    {live ? (
                      <Link to={`/product/${item.product_slug}`} data-testid={`review-product-${i}`} className="transition-colors hover:text-gold-dark">
                        {item.product_name}{item.location ? ` · ${item.location}` : ""}
                      </Link>
                    ) : (
                      item.r
                    )}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  const { t } = useLanguage();
  return (
    <section data-testid="final-cta-section" className="relative overflow-hidden bg-navy py-24 text-center text-white noise-bg lg:py-32">
      <div className="spotlight absolute inset-0" />
      <div className="relative mx-auto max-w-3xl px-6">
        <Reveal>
          <p className="font-display text-4xl font-light italic tracking-tight sm:text-5xl">{t.hero.l1}</p>
        </Reveal>
        <Reveal delay={1}>
          <p className="mt-2 font-display text-4xl font-light tracking-tight text-gold-light sm:text-5xl">{t.hero.l2}</p>
        </Reveal>
        <Reveal delay={2} className="mt-12">
          <Link to="/shop" data-testid="final-cta-button" className="btn-gold-luxe">
            {t.actions.shopNow}
            <ArrowRight size={14} className="rtl:rotate-180" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <TrustBar />
      <Categories />
      <EditorialMarquee />
      <Manifesto />
      <Featured />
      <ComingSoon />
      <Bestsellers />
      <ArenaLineup />
      <Testimonials />
      <FinalCta />
    </div>
  );
}
